/**
 * NotificationBell — canonical Notification Center dropdown.
 *
 * Sits in the sidebar identity area. Polls /api/notifications/unread-count
 * every 45s, opens a scrollable panel with the 20 most recent alerts,
 * supports mark-one-read / mark-all-read / deep-link on click.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2, ChevronRight } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60)     return 'just now';
  if (s < 3600)   return `${Math.floor(s / 60)}m`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CATEGORY_TINT = {
  leads:         'bg-[#F56F05]',
  tasks:         'bg-emerald-500',
  jobs:          'bg-blue-500',
  communication: 'bg-purple-500',
};

const NotificationBell = ({ compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/api/notifications/unread-count`, { headers });
      const c = r.data?.count || 0;
      setCount(c);
      // App badging (spec §8) — synchronize with canonical unread count.
      if ('setAppBadge' in navigator) {
        try { c > 0 ? navigator.setAppBadge(c) : navigator.clearAppBadge(); } catch { /* noop */ }
      }
    } catch { /* silent */ }
     
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/notifications?limit=20`, { headers });
      setItems(r.data?.notifications || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
     
  }, [token]);

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(fetchUnread, 45000);
    return () => clearInterval(iv);
  }, [fetchUnread]);

  useEffect(() => { if (open) fetchList(); }, [open, fetchList]);

  const markAll = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-all-read`, {}, { headers });
      setItems(items.map(n => ({ ...n, read: true })));
      setCount(0);
      if ('clearAppBadge' in navigator) { try { navigator.clearAppBadge(); } catch { /* noop */ } }
    } catch { /* silent */ }
  };

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await axios.post(`${API}/api/notifications/${n.id}/read`, {}, { headers });
        setCount(c => Math.max(0, c - 1));
      } catch { /* non-fatal */ }
    }
    if (n.deep_link) {
      navigate(n.deep_link);
    }
  };

  const buttonBase = compact
    ? 'w-9 h-9 rounded-md hover:bg-zinc-800/60 text-zinc-300 hover:text-white'
    : 'w-8 h-8 rounded-md hover:bg-zinc-800/60 text-zinc-400 hover:text-white';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          className={`relative inline-flex items-center justify-center ${buttonBase} transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F56F05] cursor-pointer`}
          aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
          data-testid="notification-bell"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        >
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F56F05] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-zinc-900"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="right"
        sideOffset={12}
        className="w-[360px] p-0 max-h-[520px] overflow-hidden"
        data-testid="notification-center"
      >
        <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <p className="text-[13px] font-bold text-zinc-900">Notifications</p>
          <button
            type="button"
            className="text-[11px] font-bold text-[#F56F05] hover:underline disabled:opacity-40"
            onClick={markAll}
            disabled={count === 0}
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-3 h-3 inline mr-0.5" /> Mark all read
          </button>
        </div>
        <div className="overflow-y-auto max-h-[440px]">
          {loading ? (
            <div className="py-10 text-center text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center px-5" data-testid="notification-empty">
              <Bell className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
              <p className="text-[12px] font-semibold text-zinc-700">You&rsquo;re all caught up.</p>
              <p className="text-[10px] text-zinc-500 mt-1">Alerts about leads, tasks and job changes will appear here.</p>
            </div>
          ) : (
            items.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                data-testid={`notification-row-${n.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 flex gap-2.5 ${n.read ? '' : 'bg-orange-50/40'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${CATEGORY_TINT[n.category] || 'bg-zinc-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <p className={`text-[12px] truncate ${n.read ? 'text-zinc-600 font-medium' : 'text-zinc-900 font-bold'}`}>
                      {n.title}
                    </p>
                    <span className="text-[10px] text-zinc-400 flex-shrink-0 ml-auto">{timeAgo(n.created_at)}</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 truncate">{n.message}</p>
                </div>
                {n.deep_link && <ChevronRight className="w-3 h-3 text-zinc-300 flex-shrink-0 self-center" />}
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
